/**
 * Pure helpers for filtering and inspecting failed bank-charge attempt audit rows.
 *
 * These helpers derive all state from `data.audit` (the existing payment_transaction_audit
 * array already fetched by the BFF route) and have no side effects.
 */

const CHARGE_FAILED_EVENT = 'charge-failed';
const CHARGE_ATTEMPTS_CAP = 10;

/**
 * Returns the most recent failed bank-charge attempts from the audit log,
 * sorted newest first, capped at CHARGE_ATTEMPTS_CAP.
 *
 * Tolerates a null/undefined audit array, and rows with missing or malformed
 * extra_data (code/message may be undefined on those rows).
 *
 * @param {Array|null|undefined} audit - array of payment_transaction_audit rows
 * @returns {Array} filtered, sorted, capped charge-failed rows
 */
export function getRecentFailedChargeAttempts(audit) {
  if (!Array.isArray(audit)) {
    return [];
  }
  return audit
    .filter(row => row && row.event === CHARGE_FAILED_EVENT)
    .slice()
    .sort((a, b) => {
      const da = a.created ? new Date(a.created).getTime() : 0;
      const db = b.created ? new Date(b.created).getTime() : 0;
      return db - da;
    })
    .slice(0, CHARGE_ATTEMPTS_CAP);
}

/**
 * Returns true when the most recent charge-failed audit row has
 * extra_data.code === 'NoBillableBankInstrument'.
 *
 * Intentionally delegates to getRecentFailedChargeAttempts so the pill and the history
 * table can never disagree on which attempt is newest.
 *
 * @param {Array|null|undefined} audit - array of payment_transaction_audit rows
 * @returns {boolean}
 */
export function isBillingBlockedByNoBillableBank(audit) {
  const newest = getRecentFailedChargeAttempts(audit)[0];
  if (!newest) {
    return false;
  }
  const extraData = newest.extra_data;
  if (!extraData || typeof extraData !== 'object') {
    return false;
  }
  return extraData.code === 'NoBillableBankInstrument';
}

/**
 * Returns true when the transaction view should show the "billing blocked" status
 * pill in place of any allow-bill call to action.
 *
 * Callers pass the two view-level predicates as booleans so this stays pure and
 * testable. Every allow-bill CTA is gated on the negation of this same value, so
 * a call to action is only ever suppressed when the pill takes its place.
 *
 * @param {object} args
 * @param {boolean} args.canRebill - transaction is in a rebillable state
 * @param {boolean} args.isAds - transaction is an ADS transaction (no bank charge)
 * @param {Array|null|undefined} args.audit - payment_transaction_audit rows
 * @returns {boolean}
 */
export function shouldShowBillingBlockedPill({ canRebill, isAds, audit }) {
  return Boolean(canRebill) && !isAds && isBillingBlockedByNoBillableBank(audit);
}
